import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, FileText, Filter, AlertTriangle } from "lucide-react";
import { statusMeta, fmtDate, fmtDays } from "@/pages/hr/leave/leaveUtils";

const STATUSES = ["pending", "approved", "rejected", "cancel_requested", "cancelled", "draft"];

export default function LeaveApplicationsTab({ employees = [], types = [] }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", employee_id: "", leave_type_id: "", date_from: "", date_to: "" });
  const [review, setReview] = useState(null); // {app, action}
  const [notes, setNotes] = useState("");

  const load = () => {
    const q = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/hr/leave/applications${q ? "?" + q : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const downloadAttachment = async (id) => {
    try { const res = await api.get(`/hr/leave/applications/${id}/attachment`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error("Could not open attachment"); }
  };

  const submitReview = async () => {
    const { app, action } = review;
    if (action === "reject" && !notes.trim()) return toast.error("A rejection reason is required");
    try {
      if (action === "cancel_confirm" || action === "cancel_reject") {
        await api.post(`/hr/leave/applications/${app.id}/cancel-confirm`, { action: action === "cancel_confirm" ? "confirm" : "reject", notes });
      } else {
        await api.post(`/hr/leave/applications/${app.id}/review`, { action, notes, rejection_reason: action === "reject" ? notes : "" });
      }
      toast.success("Done"); setReview(null); setNotes(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="leave-applications-tab">
      <div className="flex flex-wrap gap-2 mb-3 items-end rounded-lg border border-slate-200 bg-white p-3">
        <Filter className="w-4 h-4 text-slate-400 mb-2" />
        <div><Label className="text-xs">Status</Label>
          <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-40" data-testid="leave-filter-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{statusMeta(s).label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Employee</Label>
          <Select value={f.employee_id || "all"} onValueChange={(v) => setF({ ...f, employee_id: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-48" data-testid="leave-filter-employee"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All employees</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Type</Label>
          <Select value={f.leave_type_id || "all"} onValueChange={(v) => setF({ ...f, leave_type_id: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-36" data-testid="leave-filter-type"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">From</Label><Input type="date" className="h-8" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></div>
        <div><Label className="text-xs">To</Label><Input type="date" className="h-8" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="leave-applications-table">
          <thead><tr><th>Employee</th><th>Type</th><th>Dates</th><th>Days</th><th>After</th><th>Status</th><th>Reason</th><th>Att.</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={9} className="text-center text-slate-400 py-6">No applications</td></tr>
              : rows.map((a) => (
                <tr key={a.id} data-testid={`leave-app-row-${a.id}`}>
                  <td className="text-xs"><span className="font-mono">{a.employee_id}</span><br />{a.full_name}</td>
                  <td className="text-xs">{a.leave_type_code}{a.half_day ? " ½" : ""}</td>
                  <td className="text-xs font-mono">{fmtDate(a.start_date)}<br />– {fmtDate(a.end_date)}</td>
                  <td className="text-center">{fmtDays(a.requested_days)}</td>
                  <td className={`text-center ${a.negative_balance ? "text-rose-600 font-semibold" : ""}`}>{fmtDays(a.balance_after)}{a.negative_balance && <AlertTriangle className="w-3 h-3 inline ml-0.5" />}</td>
                  <td><Badge variant="outline" className={statusMeta(a.status).cls}>{statusMeta(a.status).label}</Badge></td>
                  <td className="text-xs max-w-[160px] truncate" title={a.reason}>{a.reason || "—"}</td>
                  <td className="text-center">{a.has_attachment ? <button onClick={() => downloadAttachment(a.id)} data-testid={`leave-att-${a.id}`}><FileText className="w-4 h-4 text-sky-600" /></button> : "—"}</td>
                  <td>
                    {can("leave:approve") && a.status === "pending" && (
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`leave-approve-${a.id}`} onClick={() => { setReview({ app: a, action: "approve" }); setNotes(""); }}><Check className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700" data-testid={`leave-reject-${a.id}`} onClick={() => { setReview({ app: a, action: "reject" }); setNotes(""); }}><X className="w-3 h-3" /></Button>
                      </div>
                    )}
                    {can("leave:approve") && a.status === "cancel_requested" && (
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7 text-xs bg-violet-600 hover:bg-violet-700" data-testid={`leave-cancel-confirm-${a.id}`} onClick={() => { setReview({ app: a, action: "cancel_confirm" }); setNotes(""); }}>Confirm Cancel</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`leave-cancel-reject-${a.id}`} onClick={() => { setReview({ app: a, action: "cancel_reject" }); setNotes(""); }}>Keep</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!review} onOpenChange={(v) => !v && setReview(null)}>
        <DialogContent className="max-w-md" data-testid="leave-review-dialog">
          <DialogHeader><DialogTitle>{review?.action === "approve" ? "Approve Leave" : review?.action === "reject" ? "Reject Leave" : review?.action === "cancel_confirm" ? "Confirm Cancellation" : "Reject Cancellation"}</DialogTitle></DialogHeader>
          {review?.app && <p className="text-sm text-slate-500">{review.app.employee_id} · {review.app.leave_type_name} · {fmtDate(review.app.start_date)}–{fmtDate(review.app.end_date)} ({fmtDays(review.app.requested_days)}d)</p>}
          {review?.app?.negative_balance && review?.action === "approve" && <div className="text-xs text-rose-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Negative Balance Request — balance after approval: {fmtDays(review.app.balance_after)}</div>}
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="leave-review-notes"
            placeholder={review?.action === "reject" ? "Rejection reason (required)" : "Notes (optional)"} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)}>Close</Button>
            <Button onClick={submitReview} data-testid="leave-review-submit" className="bg-slate-900">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
